import type { paths } from "@/types/api";

type ApiPath = keyof paths;

function endpoint<const T extends ApiPath>(path: T): T {
  return path;
}

export const API_ENDPOINTS = {
  SUBMISSIONS: {
    LIST: endpoint("/api/submissions"),
    EXPORT: endpoint("/api/submissions/export"),
    DETAIL: (id: string) => `/api/submissions/${id}` as const,
    // CR-001 FR-1.8 / FR-1.9 — authenticated download, served with Content-Disposition: attachment.
    ATTACHMENT: (id: string) => `/api/submissions/${id}/attachment` as const,
  },
  AUDIT_LOGS: {
    LIST: endpoint("/api/audit-logs"),
    DETAIL: (id: string) => `/api/audit-logs/${id}` as const,
    EXPORT: endpoint("/api/audit-logs/export"),
  },
  BACKUPS: {
    LIST: endpoint("/api/backups"),
    CREATE: endpoint("/api/backups"),
    DOWNLOAD: (id: string) => `/api/backups/${id}/download` as const,
    RESTORE: (id: string) => `/api/backups/${id}/restore` as const,
    SCHEDULE: endpoint("/api/backups/schedule"),
  },
  /**
   * FAQ screen. Written as plain strings rather than through `endpoint()` on purpose: that helper
   * is typed against the VENDORED OpenAPI paths (src/types/api.ts), which cannot know about
   * /api/faqs until `pnpm openapi:generate` is run against a backend carrying this feature.
   * Switch them to `endpoint()` once the spec is regenerated — the types will then guard them.
   */
  FAQS: {
    CATEGORIES: "/api/faqs/categories",
    CATEGORY: (key: string) => `/api/faqs/categories/${key}` as const,
    QUESTIONS: "/api/faqs/questions",
    QUESTION: (code: string) => `/api/faqs/questions/${code}` as const,
  },
  CONTENT: {
    LIST: endpoint("/api/content"),
    CREATE: (contentType: string) => `/api/content/${contentType}` as const,
    DETAIL: (contentType: string, id: string) =>
      `/api/content/${contentType}/${id}` as const,
    APPROVE: (contentType: string, id: string) =>
      `/api/content/${contentType}/${id}/approve` as const,
    PUBLISH: (contentType: string, id: string) =>
      `/api/content/${contentType}/${id}/publish` as const,
    REJECT: (contentType: string, id: string) =>
      `/api/content/${contentType}/${id}/reject` as const,
    SET_DEPARTMENT: (contentType: string, id: string) =>
      `/api/content/${contentType}/${id}/department` as const,
    ASSIGN_DEPARTMENT_BULK: (contentType: string) =>
      `/api/content/${contentType}/assign-department` as const,
    PUBLISH_BULK: (contentType: string) =>
      `/api/content/${contentType}/publish` as const,
    SUBMIT: (contentType: string, id: string) =>
      `/api/content/${contentType}/${id}/submit` as const,
    VERSIONS: (contentType: string, id: string) =>
      `/api/content/${contentType}/${id}/versions` as const,
    VERSION: (contentType: string, id: string, versionId: string) =>
      `/api/content/${contentType}/${id}/versions/${versionId}` as const,
    ROLLBACK: (contentType: string, id: string, versionId: string) =>
      `/api/content/${contentType}/${id}/rollback/${versionId}` as const,
    EXPORT: endpoint("/api/content/export"),
  },
  DEPARTMENTS: {
    LIST: endpoint("/api/departments"),
    CREATE: endpoint("/api/departments"),
    DETAIL: (id: string) => `/api/departments/${id}` as const,
    SETTINGS: (id: string) => `/api/departments/${id}/settings` as const,
    ASSIGN_MANAGER: (id: string) =>
      `/api/departments/${id}/assign-manager` as const,
    SUB_ADMIN: (id: string) => `/api/departments/${id}/sub-admin` as const,
    WORKFLOW: (id: string) => `/api/departments/${id}/workflow` as const,
  },
  DOCUMENTS: {
    LIST: endpoint("/api/documents"),
    SCOPE: "/api/documents/scope" as const,
    DETAIL: (id: string) => `/api/documents/${id}` as const,
    PERMISSIONS: (id: string) => `/api/documents/${id}/permissions` as const,
    UPLOAD: endpoint("/api/documents/upload"),
    EXPORT: "/api/documents/export" as const,
  },
  OBS_DATASETS: {
    LIST: "/api/obs-datasets" as const,
    VALIDATE: (slug: string) => `/api/obs-datasets/${slug}/validate` as const,
    UPLOAD: (slug: string, force = false) =>
      `/api/obs-datasets/${slug}/upload${force ? "?force=true" : ""}` as const,
    ACTIVATE: (slug: string, version: number) =>
      `/api/obs-datasets/${slug}/versions/${version}/activate` as const,
  },
  DELEGATIONS: {
    LIST: "/api/delegation-datasets" as const,
    VALIDATE: (slug: string) => `/api/delegation-datasets/${slug}/validate` as const,
    UPLOAD: (slug: string, force = false) =>
      `/api/delegation-datasets/${slug}/upload${force ? "?force=true" : ""}` as const,
    ACTIVATE: (slug: string, version: number) =>
      `/api/delegation-datasets/${slug}/versions/${version}/activate` as const,
  },
  OBS_REPORTS: {
    LIST: "/api/obs-reports" as const,
    CREATE: "/api/obs-reports" as const,
    PROFILE: "/api/obs-reports/profile" as const,
    DETAIL: (slug: string) => `/api/obs-reports/${slug}` as const,
    UPDATE: (slug: string) => `/api/obs-reports/${slug}` as const,
    DELETE: (slug: string) =>
      `/api/obs-reports/${slug}?confirm=${encodeURIComponent(slug)}` as const,
    TILE: (slug: string, locale: "ar" | "en" = "ar") =>
      `/api/obs-reports/${slug}/tile?locale=${locale}` as const,
    CHART_OPTIONS: (slug: string) =>
      `/api/obs-reports/${slug}/chart-options` as const,
  },
  DOCUMENT_LIBRARIES: {
    LIST: "/api/document-libraries" as const,
    CREATE: "/api/document-libraries" as const,
    DETAIL: (id: string) => `/api/document-libraries/${id}` as const,
  },
  NOTIFICATIONS: {
    // Not in the generated OpenAPI schema (stale, pre-dates this endpoint).
    LIST: "/api/notifications/me" as const,
    UNREAD_COUNT: "/api/notifications/me/unread-count" as const,
    DETAIL: (id: string) => `/api/notifications/${id}` as const,
    MARK_READ: (id: string) => `/api/notifications/${id}/read` as const,
    MARK_ALL_READ: "/api/notifications/read-all" as const,
    PREFERENCES: "/api/notifications/preferences/me" as const,
  },
  REPORTS: {
    // Not in the generated OpenAPI schema (stale, pre-dates this endpoint).
    SCOPE: "/api/reports/scope" as const,
    CONTENT: endpoint("/api/reports/content"),
    USERS: endpoint("/api/reports/users"),
    EXPORT: endpoint("/api/reports/export"),
    USER_ACTIVITY_LOGINS: "/api/reports/user-activity/logins" as const,
    USER_ACTIVITY_AUDIT: "/api/reports/user-activity/audit" as const,
    // Not in the generated OpenAPI schema (stale, pre-dates these endpoints).
    WEBSITE_ANALYTICS: "/api/reports/website-analytics" as const,
    WEBSITE_ANALYTICS_CONFIG: "/api/reports/website-analytics/config" as const,
  },
  SETTINGS: {
    ROOT: endpoint("/api/settings"),
    MAINTENANCE: endpoint("/api/settings/maintenance"),
    // Not in the generated OpenAPI schema (stale, pre-dates this endpoint) — same
    // reasoning as the dynamic-id endpoints below that bypass endpoint()'s `keyof paths` check.
    EMAIL_TEST: "/api/settings/email/test" as const,
  },
  USERS: {
    LIST: endpoint("/api/users"),
    CREATE: endpoint("/api/users"),
    DETAIL: (id: string) => `/api/users/${id}` as const,
    ACTIVITY: (id: string) => `/api/users/${id}/activity` as const,
    DELETE: (id: string) => `/api/users/${id}/delete` as const,
    DEACTIVATE: (id: string) => `/api/users/${id}/deactivate` as const,
    ROLE: (id: string) => `/api/users/${id}/role` as const,
    INVITE: endpoint("/api/users/invite"),
  },
  PERMISSIONS: {
    MATRIX: "/api/permissions/matrix" as const,
    MATRIX_ROLES: "/api/permissions/matrix/roles" as const,
    ME: "/api/permissions/me" as const,
    USER: (id: string) => `/api/permissions/users/${id}` as const,
  },
  ROLES: {
    LIST: "/api/roles" as const,
    CREATE: "/api/roles" as const,
    DETAIL: (code: string) => `/api/roles/${encodeURIComponent(code)}` as const,
  },
} as const;
